import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Link,
  Select,
  Text,
  Textarea,
  useBreakpointValue,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useLocation, useNavigate } from "react-router-dom";
import Footer from "../../components/common/Footer";
import Header from "../../components/common/Header";
import HelpdeskSideNav from "../../components/common/helpdeskSidenav";
import trainerRegistration from "../../services/trainer/trainerRegistration";
import routes from "../routes";

const NewRequest = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [logoFile, setlogoFile] = useState<File | null>(null);
  const [batchFile, setbatchFile] = useState<FormData | null>(null);
  const [filename, setFilename] = useState("");
  const [preview, setPreview] = useState<string | undefined>();
  const [priorityUnit, setPriorityUnit] = useState<any[]>([]);

  const showSideNav = useBreakpointValue({ base: false, lg: true });

  const location = useLocation();

  useEffect(() => {
    const priorityUnit = [
      {
        id: "high",
        title: "High",
      },
      {
        id: "normal",
        title: "Normal",
      },
      {
        id: "low",
        title: "Low",
      },
    ];
    setPriorityUnit(priorityUnit);
  }, []);

  useEffect(() => {
    if (!logoFile) {
      setPreview(undefined);
      return;
    }
    const objectUrl = URL.createObjectURL(logoFile);
    setPreview(objectUrl);

    // free memory when ever this component is unmounted
    return () => URL.revokeObjectURL(objectUrl);
  }, [batchFile]);

  const activeLink = (link: string) => {
    const style = {
      borderColor: "white",
      borderLeft: "",
      bgColor: "white",
      textColor: "#9E9E9E",
    };

    if (link === location.pathname) {
      style.borderColor = "#EC3337";
      style.borderLeft = "3px Solid";
      style.bgColor = "#FFFAFA";
      style.textColor = " #EC3337";
    }

    return style;
  };

  const baseStyle = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
    borderWidth: 2,
    borderRadius: 2,
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
  };

  const activeStyle = {
    borderColor: "#2196f3",
  };

  const acceptStyle = {
    borderColor: "#00e676",
  };

  const rejectStyle = {
    borderColor: "#ff1744",
  };
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (!acceptedFiles.length) {
      toast({
        title: "File Upload",
        description: "Invalid file uploaded",
        status: "error",
      });
      setbatchFile(null);
    } else {
      const formData = new FormData();
      const file = acceptedFiles[0];

      formData.append("attachment", file);
      setbatchFile(formData);
      setFilename(file.name);
      toast({
        title: "File Upload",
        description: "Your file is ready for upload",
        status: "success",
      });
    }
  }, []);
  //   const submitCV = async () => {
  //       const formData = new FormData()
  //       if (batchFile) {
  //           const file = batchFile

  //           formData.append('doc', file, file.name)
  //           try {
  //               const cvupload = await trainerRegistration.uploadCv(formData);
  //           if (cvupload) {

  //               toast({
  //                   title: "Registration",
  //                   description: "CV uploaded successfully",
  //                   status: "success",
  //               })
  //           }
  //           } catch (error) {

  //               toast({
  //                   title: "Registration",
  //                   description: "something went wrong, try again later",
  //                   status: "error",
  //               })
  //           }

  //       }
  //   }

  const handleButtonClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    formik.handleSubmit();
  };

  const formik = useFormik({
    validate: (values: any) => {
      const errors: any = {};
      if (!values.name) {
        errors.name = "Full name is required";
      }
      if (!values.subject) {
        errors.subject = "Subject is required";
      }
      if (!values.email) {
        errors.email = " Email Address is required";
      }
      if (!values.priority_level) {
        errors.priority_level = "Priority Level is required";
      }
      if (!values.message) {
        errors.message = "Description is required";
      }

      return errors;
    },
    initialValues: {
      name: "",
      subject: "",
      email: "",
      priority_level: "",
      message: "",
    },
    onSubmit: async (values) => {
      const formData = new FormData();
      const file = batchFile;

      // formData.append("attachment", file, filename);
      formData.append("name", values.name);
      formData.append("subject", values.subject);
      formData.append("email", values.email);
      formData.append("priority_level", values.priority_level);
      formData.append("message", values.message);

      // Append the file if it exists
      if (batchFile) {
        const file = batchFile.get("attachment");
        if (file) {
          formData.append("attachment", file);
        }
      }

      try {
        const basicResponse = await trainerRegistration.createHelpRequest(
          formData
        );
        if (basicResponse) {
          toast({
            title: "HelpDesk",
            description: "Help desk request submitted successfully.",
            status: "success",
          });
          formik.resetForm();
          navigate(-1);
        } else {
          toast({
            title: "HelpDesk",
            description: `${basicResponse.error}`,
            status: "error",
          });
        }
      } catch (error: any) {
        toast({
          title: "HelpDesk",
          description: `${error.response.data.message}`,
          status: "error",
        });
      }
    },
  });

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({ onDrop });
  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  return (
    <>
      <Header />
      <Center
        bgColor="#3362AB"
        width="100%"
        textAlign="center"
        pt={["30px", "72px"]}
        height={["320px", "350px", "400px"]}
        position={"relative"}
      >
        <Text color="white" fontSize={["40px", "48px"]} fontWeight="800">
          Submit New Help Request
        </Text>
      </Center>

      {!showSideNav && (
        <HStack
          // overflowX="scroll"
          width={"100%"}
          height={"60px"}
          justifyContent={"space-around"}
          alignItems={"center"}
          fontSize={"13px"}
          padding={"0px 8px"}
        >
          <Link
            onClick={() => navigate(routes.HomeHelpdesk)}
            textAlign={"center"}
            alignContent={"center"}
            height={"60px"}
            borderBottom={activeLink(routes.HomeHelpdesk).borderLeft}
            borderColor={activeLink(routes.HomeHelpdesk).borderColor}
            color={activeLink(routes.HomeHelpdesk).textColor}
          >
            Help Desk Home{" "}
          </Link>
          <Divider orientation="vertical" height={"70%"} />
          <Link
            onClick={() => navigate(routes.HelpdeskNewRequest)}
            textAlign={"center"}
            alignContent={"center"}
            height={"60px"}
            borderBottom={activeLink(routes.HelpdeskNewRequest).borderLeft}
            borderColor={activeLink(routes.HelpdeskNewRequest).borderColor}
            color={activeLink(routes.HelpdeskNewRequest).textColor}
          >
            New Help Request
          </Link>
          <Divider orientation="vertical" height={"70%"} />
          <Link
            onClick={() => navigate(routes.PaymentGuidelines)}
            textAlign={"center"}
            alignContent={"center"}
            height={"60px"}
            borderBottom={activeLink(routes.PaymentGuidelines).borderLeft}
            borderColor={activeLink(routes.PaymentGuidelines).borderColor}
            color={activeLink(routes.PaymentGuidelines).textColor}
          >
            Payment Guideline
          </Link>
        </HStack>
      )}

      <Box bg="#F9F8F8" height="100%">
        <Flex width="90%" mx="auto" pt="80px">
          {showSideNav && <HelpdeskSideNav />}
          {/* <form onSubmit={formik.handleSubmit}> */}
          <VStack
            width={["90%", "70%", "60%"]}
            minHeight="90vh"
            height="auto"
            mb="4rem"
            mx="auto"
            bg="white"
            padding={["1.5rem", "3.4rem"]}
          >
            <HStack mt={6} mb="25px" width="100%">
              <FormControl
                isInvalid={
                  formik.errors.name && formik.touched.name ? true : false
                }
                width="50%"
              >
                <FormLabel fontWeight="700" color="brand.3" fontSize={13}>
                  Full Name
                  <Text as="span" color="red">
                    {" "}
                    *{" "}
                  </Text>
                </FormLabel>
                <Input
                  bg="inputText.500"
                  placeholder="Enter name here"
                  fontSize={12}
                  value={formik.values.name}
                  name="name"
                  onChange={formik.handleChange}
                />
                <FormErrorMessage fontSize={12}>
                  {formik.errors.name}
                </FormErrorMessage>
              </FormControl>
              <FormControl
                isInvalid={
                  formik.errors.email && formik.touched.email ? true : false
                }
                width="50%"
              >
                <FormLabel fontWeight="700" color="brand.3" fontSize={13}>
                  Email{" "}
                  <Text as="span" color="red">
                    {" "}
                    *{" "}
                  </Text>
                </FormLabel>
                <Input
                  bg="inputText.500"
                  placeholder="Enter email here"
                  fontSize={12}
                  value={formik.values.email}
                  name="email"
                  onChange={formik.handleChange}
                />
                <FormErrorMessage fontSize={12}>
                  {formik.errors.email}
                </FormErrorMessage>
              </FormControl>
            </HStack>
            <HStack mb="25px" width="100%">
              <FormControl
                isInvalid={
                  formik.errors.subject && formik.touched.subject ? true : false
                }
                width="50%"
              >
                <FormLabel fontWeight="700" color="brand.3" fontSize={13}>
                  Subject
                  <Text as="span" color="red">
                    {" "}
                    *{" "}
                  </Text>
                </FormLabel>
                <Input
                  bg="inputText.500"
                  placeholder="Enter title"
                  fontSize={12}
                  value={formik.values.subject}
                  name="subject"
                  onChange={formik.handleChange}
                />
                <FormErrorMessage fontSize={12}>
                  {formik.errors.subject}
                </FormErrorMessage>
              </FormControl>
              <FormControl
                isInvalid={
                  formik.errors.priority_level && formik.touched.priority_level
                    ? true
                    : false
                }
                width="50%"
              >
                <FormLabel fontWeight="700" color="brand.3" fontSize={13}>
                  Priority Level{" "}
                  <Text as="span" color="red">
                    {" "}
                    *{" "}
                  </Text>
                </FormLabel>
                {/* <Input
                  bg="inputText.500"
                  placeholder="Enter priority level here"
                  fontSize={12}
                /> */}
                <Select
                  variant="outline"
                  bg="inputText.500"
                  size="md"
                  fontSize={12}
                  placeholder="Select priority Level"
                  value={formik.values.priority_level}
                  name="priority_level"
                  onChange={(e) => {
                    formik.handleChange(e);
                  }}
                  color="brand.subtitle"
                >
                  {priorityUnit.map((each) => (
                    <option key={each.id} value={each.id}>
                      {" "}
                      {each.title}{" "}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage fontSize={12}>
                  {formik.errors.priority_level}
                </FormErrorMessage>
              </FormControl>
            </HStack>
            <FormControl
              isInvalid={
                formik.errors.message && formik.touched.message ? true : false
              }
              mb="25px"
            >
              <FormLabel fontWeight="700" fontSize={13}>
                Write here
              </FormLabel>
              <Textarea
                height="150px"
                value={formik.values.message}
                name="message"
                onChange={formik.handleChange}
              />
              <FormErrorMessage fontSize={12}>
                {formik.errors.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl mb="25px">
              <Text fontWeight="700" fontSize={13}>
                Attachment (if any)
              </Text>
              <Box {...getRootProps({ style })} height="150px">
                <input {...getInputProps()} />
                {isDragActive ? (
                  <p>Drop the files here ...</p>
                ) : (
                  <p
                    style={{
                      maxWidth: "200px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      boxSizing: "border-box",
                    }}
                  >
                    {filename !== "" ? (
                      <Text>{filename}</Text>
                    ) : (
                      <span>Drag and drop or browse file</span>
                    )}
                  </p>
                )}
              </Box>
            </FormControl>
            {filename && (
              <Text fontWeight="400" color="#959595" fontSize="12px">
                {filename}
              </Text>
            )}
            <Flex justifyContent="flex-end" gap="2rem">
              <Button
                mt={4}
                // type="submit"
                colorScheme="red"
                isLoading={formik.isSubmitting}
                size="lg"
                px={6}
                fontSize="14px"
                borderRadius="0px"
                onClick={handleButtonClick}
              >
                {" "}
                SUBMIT
              </Button>
              <Button
                mt={4}
                colorScheme="red"
                size="lg"
                px={6}
                fontSize="14px"
                borderRadius="0px"
                onClick={() => formik.resetForm()}
              >
                {" "}
                CANCEL
              </Button>
            </Flex>
          </VStack>
          {/* </form> */}
        </Flex>
      </Box>
      <Footer />
    </>
  );
};

export default NewRequest;
